import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Checkbox } from '@/components/ui/checkbox';
import type { OnboardingStepProps } from './types';
import { useToast } from '@/hooks/use-toast';
import { Phone, Shield, Info, CheckCircle, ArrowRight } from 'lucide-react';

type SetupStep = 'intro' | 'phone' | 'verify';

const TwoFactorSetup: React.FC<OnboardingStepProps> = ({ onNext, onBack, isFirstStep, isLastStep }) => {
  const [step, setStep] = useState<SetupStep>('intro');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const { toast } = useToast();

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
    }
    return value;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handlePhoneSubmit = () => {
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    if (!cleanedPhone || cleanedPhone.length !== 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Verification Code Sent",
      description: `Please check your phone (${phoneNumber}) for the 6-digit verification code.`,
    });
    setStep('verify');
  };

  const handleVerifyCode = () => {
    if (verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the complete 6-digit verification code.",
        variant: "destructive",
      });
      return;
    }

    if (rememberDevice) {
      localStorage.setItem('vitl-device-remembered', 'true');
    }

    toast({
      title: "Two-Factor Authentication Enabled!",
      description: "Your account is now secured with 2FA.",
    });

    onNext();
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-sm">
      <div className="max-w-md mx-auto">
        {step === 'intro' ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-vitl-teal/10 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-10 h-10 text-vitl-teal" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Secure Your Account</h3>
              <p className="text-gray-600 mb-8">
                Two-factor authentication adds an extra layer of security to your account
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-900 mb-1">EPCS Compliance Required</p>
                  <p className="text-blue-700">
                    Two-factor authentication is mandated by the DEA for Electronic Prescriptions 
                    for Controlled Substances (EPCS) to ensure secure prescription handling.
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Protects against unauthorized access</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Required for EPCS compliance</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Quick verification via SMS</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between gap-4">
              {!isFirstStep ? (
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="px-8"
                >
                  Back
                </Button>
              ) : (
                <div></div>
              )}
              <Button
                onClick={() => setStep('phone')}
                className="bg-vitl-green hover:bg-vitl-green/90 text-white px-8"
              >
                Enable Two-Factor Authentication
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : step === 'phone' ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-vitl-teal/10 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-8 h-8 text-vitl-teal" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Enter Your Phone Number</h3>
              <p className="text-gray-600">
                We'll send verification codes to this number when you sign in
              </p>
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Phone Number
              </label>
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                className="text-center text-lg"
                maxLength={14}
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                US phone numbers only. Standard SMS rates may apply.
              </p>
            </div>
            
            <div className="flex justify-between gap-4">
              <Button
                onClick={() => setStep('intro')}
                variant="outline"
                className="px-8"
              >
                Back
              </Button>
              <Button
                onClick={handlePhoneSubmit}
                className="bg-vitl-green hover:bg-vitl-green/90 text-white px-8"
                disabled={phoneNumber.replace(/\D/g, '').length !== 10}
              >
                Send Verification Code
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2">Enter Verification Code</h3>
              <p className="text-gray-600 mb-6">
                Enter the 6-digit code we sent to {phoneNumber}
              </p>
            </div>
            
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={(value) => setVerificationCode(value)}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            
            <div className="flex items-center space-x-2 justify-center">
              <Checkbox 
                id="remember-device" 
                checked={rememberDevice}
                onCheckedChange={(checked) => setRememberDevice(checked as boolean)}
              />
              <label 
                htmlFor="remember-device" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Remember this device for 30 days
              </label>
            </div>
            
            <div className="flex justify-between gap-4">
              <Button
                onClick={() => setStep('phone')}
                variant="outline"
                className="px-8"
              >
                Back
              </Button>
              <Button
                onClick={handleVerifyCode}
                className="bg-vitl-green hover:bg-vitl-green/90 text-white px-8"
                disabled={verificationCode.length !== 6}
              >
                {isLastStep ? 'Complete Setup' : 'Verify and Continue'}
              </Button>
            </div>
            
            <div className="text-center">
              <button
                onClick={handlePhoneSubmit}
                className="text-sm text-vitl-teal hover:underline"
              >
                Didn't receive a code? Resend
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;